import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getProductTrees } from '@/api/core/productTree';
import { log } from '@/utils/core/vueLogger.js';
import { buildCompetitorPositioning, getPositioningRules, STATEMENT_ATTRIBUTE_ENTITY_TYPE } from '@/utils/domain/competitorPositioning';

// The relevé only knows the assortment catalog (ecosVM.productsAssort), which getProducts() lists first — the
// first entry of an id is kept, never a same-id sales catalog entry.
//
// productTranslator only links a product to its competitor when COMPETITORS is already synced at translation
// time, and that copy is what gets persisted: a product translated first keeps `competitor: null` for good. So
// the competitor is resolved again here from the raw competitorId the translator now keeps.
function assortProductsById() {
	const competitorsById = new Map((iframeDataBridge.getEcosVM()?.competitors || []).map((competitor) => [competitor.id, competitor]));
	const productsById = {};
	iframeDataBridge.getProducts().forEach((product) => {
		if (product?.id == null || product.id in productsById) return;
		const competitor = product.competitor || competitorsById.get(product.competitorId) || null;
		productsById[product.id] = competitor === product.competitor ? product : { ...product, competitor };
	});
	return productsById;
}

/**
 * Positioning summary of the relevé in progress (ticket #23483). No dedicated API (spec §5): the statements
 * come straight from the legacy relevé (OPEN_COMPETITOR_POSITIONING), everything else is reference data the
 * app already holds offline — so this works identically online and offline (spec §6).
 * @param {Array<object>} statements - the relevé's statements, one per category, as sent by visitStep3-statement.js
 */
export async function getCompetitorPositioning(statements) {
	const rules = getPositioningRules(iframeDataBridge.getCustomer());
	const productTrees = await getProductTrees().catch(() => []);

	// A "ma marque" restricted to a product-tree node can't be scoped without the trees: every product would fall
	// out of it and the brand would read "absente" everywhere — show the empty state rather than wrong figures.
	if (rules.myBrandCategory && !productTrees?.length) {
		log('warn', 'CompetitorPositioning', 'No product trees available for the customer-scoped "ma marque" — nothing to show');
		return { categories: [], hasSubset: false };
	}

	const productsById = assortProductsById();
	const productTreesById = Object.fromEntries((productTrees || []).map((tree) => [tree.id, tree]));
	const statementAttributes = (iframeDataBridge.getEcosVM()?.attributes || []).filter((attribute) => attribute?.entityType === STATEMENT_ATTRIBUTE_ENTITY_TYPE);

	return buildCompetitorPositioning({
		statements: statements || [],
		productsById,
		productTreesById,
		statementAttributes,
		rules,
	});
}
